// eslint-disable-next-line
import { useCallback, useEffect, useRef, useState } from 'react';
import { loadScript } from '@paypal/paypal-js';
import axios from 'axios';
import { useDispatch, useSelector } from 'react-redux';
import Blinder from '../../../../../common/blinder/blinder';
import API_URLS from '../../../../../constants/api-urls';
import translation, { world } from '../../../../../languages';
import { AVAILABLE_WORLDS } from '../../../../../languages/constants';
import qualityEvents from '../../../../../services/quality-events/quality-events';
import isMobileAppWithDeeplinkRedirect, { isMobileAppAndroid } from '../../../../../utils/is-mobile-app';
import classNames from '../../../../../utils/classnames';
import emailHash from '../../../../../utils/email-hash';
import isProduction from '../../../../../utils/is-production';
import serializeQueryParams from '../../../../../utils/url/serialize-query-params';
import { AVAILABLE_PAYMENT_METHODS, PAYPAL_CLIENT_IDS } from '../../../payment.constants';
import { PAYMENT_METHOD_KEYS } from '../../../../../constants/payment-method-keys';
import {addFailedPaymentMethod, addPaymentError, resetErrors, serializeForm, validateForm} from '../../../reducers';
import { getMobilePathParams } from '../../kushki/utils/kushki';
import AndroidSuccessModal from '../../success-redirect/android-success-modal/components/android-success-modal';
import { getLocale } from './constants';
import { hidePaymentBlinder, showPaymentBlinder, toggleExpandClass } from '../../cart-info/cart-info.helpers';
import { getActivePaymentMethodKey } from '../../../payment.helpers';
import {getUserOS, getUserSID} from "../../../../../services/quality-events/quality-events.helpers";
import usePaymentOptions from '../../../../../services/payment/use-payment-options';

const PaypalPPCPControls = ({ t }) => {
    const user = useSelector((state) => state.main.user);
    const currencyCode = useSelector((state) => state.main.currency.code);
    const { formData, withInvoice, isSchoolMaster, activePaymentOption, paymentCart } = useSelector((state) => state.payment);
    const { data: paymentOptionsData } = usePaymentOptions(currencyCode);
    const countryCode = paymentOptionsData?.userCountry;
    const type = activePaymentOption?.id;
    const dispatch = useDispatch();
    const [showPaypalButtons, setShowPaypalButtons] = useState(true);
    const [showBlinder, setShowBlinder] = useState(false);
    const [anroidDeeplink, setAndroidDeeplink] = useState('');
    const supportedCurrency = paymentOptionsData?.currencies[PAYMENT_METHOD_KEYS.PAYPAL_CHECKOUT];
    const paymentRef = useRef({
        paypalActions: {
            disable: () => {},
            enable: () => {}
        },
        paypalSepaActions: {
            disable: () => {},
            enable: () => {}
        }
    });

    const getCurrencyCode = () => {
        const { useWorldCurrency } = serializeQueryParams();

        if (useWorldCurrency) {
            return currencyCode;
        }

        return supportedCurrency || 'EUR';
    };

    const isInvoiceFormValid = () => dispatch(validateForm(false));

    useEffect(() => {
        const closeCardModalIcon = document.getElementById('payment-cart-info-container')
            ?.getElementsByClassName('close-icon')?.[0];

        const handleCloseCardModal = () => closeCartModal();

        if (closeCardModalIcon) {
            closeCardModalIcon.addEventListener('click', handleCloseCardModal);

            return () => closeCardModalIcon.removeEventListener('click', handleCloseCardModal);
        }
    }, []);

    useEffect(() => {
        if (isInvoiceFormValid()) {
            paymentRef.current.paypalActions.enable();
            paymentRef.current.paypalSepaActions.enable();
        } else {
            paymentRef.current.paypalActions.disable();
            paymentRef.current.paypalSepaActions.disable();
        }
    }, [JSON.stringify(formData), withInvoice, isSchoolMaster]);

    useEffect(() => {
        const clientId = isProduction() ? PAYPAL_CLIENT_IDS.PRODUCTION : PAYPAL_CLIENT_IDS.STAGING;

        window.cl__p = clientId; // used for checking by auto tests

        if (!supportedCurrency || !showPaypalButtons) {
            return;
        }

        const scriptConfig = {
            clientId,
            currency: getCurrencyCode(),
            locale: getLocale(countryCode)
        };

        if (user?.email && !user.details.paypalVaultRefused) {
            scriptConfig.dataUserIdToken = user.details.paypalVaultIdToken;
        }

        loadScript(scriptConfig)
            .then((paypalSDK) => {
                renderPayPalButton(paypalSDK);
                renderCardButton(paypalSDK);
                if (world === AVAILABLE_WORLDS.DE) {
                    renderSEPAButton(paypalSDK);
                }
            })
            .catch((error) => {
                dispatch(addFailedPaymentMethod(PAYMENT_METHOD_KEYS.PAYPAL_CHECKOUT, error?.message));
                console.info('failed to load the PayPal JS SDK script', error);
            });
    }, [supportedCurrency, user.details, showPaypalButtons]);

    const handleClick = (paymentMethod) => {
        const isValid = dispatch(validateForm());

        if (isValid) {
            qualityEvents.checkoutPage.startPayment(user.id, paymentMethod, paymentCart?.finalPrice);
        }

        return Boolean(isValid);
    };

    // destroy and render again PPCP button
    const rerenderPPCPCardButton = () => {
        setShowPaypalButtons(false);
        setTimeout(() => {
            setShowPaypalButtons(true);
        }, 100);
    };

    const openCartModal = () => {
        showPaymentBlinder();
        toggleExpandClass('payment-cart-info-container');
    };

    const closeCartModal = (isAbandonedAction = true) => {
        rerenderPPCPCardButton();
        hidePaymentBlinder();
        toggleExpandClass('payment-cart-info-container');

        if (isAbandonedAction) {
            handleCancel(PAYMENT_METHOD_KEYS.PAYPAL_CHECKOUT_CREDIT_CARD);
        }
    };

    const handleCancel = (paymentMethod) => {
        qualityEvents.checkoutPage.abandonedPayment(user.id, paymentMethod, paymentCart?.finalPrice);
    };

    const renderPayPalButton = (paypalSDK) => {
        document.getElementById('paypal-holder').innerHTML = '';

        const button = paypalSDK.Buttons({
            createOrder,
            fundingSource: paypalSDK.FUNDING.PAYPAL,
            onApprove,
            onCancel: () => {
                handleCancel(PAYMENT_METHOD_KEYS.PAYPAL_CHECKOUT);
            },
            onClick: () => {
                handleClick(PAYMENT_METHOD_KEYS.PAYPAL_CHECKOUT);
            },
            onError: (err) => {
                console.info('paypal btn error', err);
                qualityEvents.checkoutPage.failedPayment(user.id, PAYMENT_METHOD_KEYS.PAYPAL_CHECKOUT, paymentCart?.finalPrice, err?.message);
            },
            onInit(data, actions) {
                const isInvoiceFormValid = dispatch(validateForm(false));

                if (!isInvoiceFormValid) {
                    actions.disable();
                }

                paymentRef.current.paypalActions = actions;
            },
            style: {
                label: 'pay'
            }
        });

        if (button.isEligible()) {
            button.render('#paypal-holder').catch((error) => {
                console.info('failed to render PayPal button', error);
            });
        } else {
            console.info('Paypal button is not eligible in this location');
        }
    };

    const renderCardButton = (paypalSDK) => {
        document.getElementById('card-holder').innerHTML = '';

        const button = paypalSDK.Buttons({
            createOrder,
            fundingSource: paypalSDK.FUNDING.CARD,
            onApprove: (data) => {
                setShowBlinder(true);
                window.scrollTo(0, 0);

                return onApprove(data);
            },
            onCancel: () => {
                closeCartModal();
            },
            onClick: () => {
                handleCardButtonClick(PAYMENT_METHOD_KEYS.PAYPAL_CHECKOUT_CREDIT_CARD);
            }
        });

        if (button.isEligible()) {
            button.render('#card-holder').catch((error) => {
                console.info('failed to render card button', error);
            });
        } else {
            console.info('Paypal card method is not eligible in this location');
        }
    };

    const renderSEPAButton = (paypalSDK) => {
        document.getElementById('sepa-holder').innerHTML = '';

        const button = paypalSDK.Buttons({
            createOrder,
            fundingSource: paypalSDK.FUNDING.SEPA,
            onApprove,
            onCancel: () => {
                handleCancel(PAYMENT_METHOD_KEYS.PAYPAL_CHECKOUT_SEPA);
            },
            onClick: () => {
                handleClick(PAYMENT_METHOD_KEYS.PAYPAL_CHECKOUT_SEPA);
            },
            onInit(data, actions) {
                const isInvoiceFormValid = dispatch(validateForm(false));

                if (!isInvoiceFormValid) {
                    actions.disable();
                }

                paymentRef.current.paypalSepaActions = actions;
            },
            style: {
                color: 'black',
                label: 'pay'
            }
        });

        if (button.isEligible()) {
            button.render('#sepa-holder').catch((error) => {
                console.info('failed to render SEPA button', error);
            });
        } else {
            console.info('Paypal SEPA method is not eligible in this location');
        }
    };

    const handleCardButtonClick = (paymentMethod) => {
        const isValid = dispatch(validateForm());

        if (isValid) {
            setShowBlinder(true);
            openCartModal();
            window.scrollTo(0, 0);

            qualityEvents.checkoutPage.startPayment(user.id, paymentMethod, paymentCart?.finalPrice);

            return true;
        }

        return false;
    };

    const catchAPIError = (response) => {
        if (response?.error) {
            dispatch(addPaymentError(response));
        } else if (response?.data?.error) {
            dispatch(addPaymentError(response.data));
        }

        qualityEvents.checkoutPage.failedPayment(
            user.id,
            getActivePaymentMethodKey(activePaymentOption?.id),
            paymentCart?.finalPrice,
            response?.data?.message || response?.data?.error || response?.error
        );

        try {
            closeCartModal(false);
        } catch (e) {}
    };

    const createOrder = useCallback(() => {
        const params = serializeQueryParams();
        const serializedForm = dispatch(serializeForm());
        const requestConfig = {
            params: {}
        };

        if (params.materialId) {
            requestConfig.params.materialId = params.materialId;
        }

        dispatch(resetErrors());
        emailHash.storeSHA256(serializedForm.email);
        const mobileParams = getMobilePathParams(true);

        return axios
            .post(
                `${API_URLS.PAYMENT_ATTEMPT}/PayPalCheckout`,
                {
                    acceptAgb: 'on',
                    currencyCode: getCurrencyCode(),
                    deepLinkRedirect: isMobileAppWithDeeplinkRedirect(),
                    invoiceRequested: false,
                    sid: getUserSID(),
                    ...(getUserOS() && { os: getUserOS() }),
                    ...mobileParams,
                    ...serializedForm
                },
                requestConfig
            )
            .then((response) => {
                paymentRef.current.orderToken = response.orderToken;
                setShowBlinder(false);

                return response.transactionId;
            })
            .catch(catchAPIError);
    }, [formData.email]);

    const onApprove = (data) => {
        const params = serializeQueryParams();

        return axios
            .post(`${API_URLS.PAYMENT_ATTEMPT_CALLBACK}/${paymentRef.current.orderToken}`, {
                deepLinkRedirect: params.appWithDeeplinkRedirect,
                transactionId: data.orderID
            })
            .then((response) => {
                handleSuccessRedirect(response.redirect);

                return response;
            })
            .catch(catchAPIError);
    };

    const handleSuccessRedirect = (url) => {
        const queryParams = serializeQueryParams();

        if (isMobileAppWithDeeplinkRedirect()) {
            if (activePaymentOption?.id === AVAILABLE_PAYMENT_METHODS.PayPalCheckoutCreditCard.id) {
                window.location.href = url;

                return;
            }

            if (
                isMobileAppAndroid() &&
                (activePaymentOption?.id === AVAILABLE_PAYMENT_METHODS.PayPalCheckout.id ||
                    activePaymentOption?.id === AVAILABLE_PAYMENT_METHODS.PayPalCheckoutSepa.id)
            ) {
                setAndroidDeeplink(url);

                return;
            }

            setTimeout(() => {
                window.location.href = url;
            }, Number(queryParams.redirectDelay || 2000));
        } else {
            window.location.href = url;
        }
    };

    if (!showPaypalButtons) {
        return <></>;
    }

    return (
        <div>
            <div className='text-left card-payment-title mt-5'>{t('payment.cardPaymentTitle')}</div>
            <div className='ppcp-container'>
                {showBlinder && <Blinder />}
                {anroidDeeplink && <AndroidSuccessModal deeplink={anroidDeeplink} />}
                <div className='button-holder position-relative'>
                    <div
                        className={classNames('paypal-checkout-button', {
                            'd-block': type === AVAILABLE_PAYMENT_METHODS.PayPalCheckout.id
                        })}
                        id='paypal-holder'
                    />
                    <div
                        className={classNames('paypal-checkout-button', {
                            'd-block': type === AVAILABLE_PAYMENT_METHODS.PayPalCheckoutSepa.id
                        })}
                        id='sepa-holder'
                    />
                    <div
                        className={classNames('paypal-checkout-button', {
                            'd-block': type === AVAILABLE_PAYMENT_METHODS.PayPalCheckoutCreditCard.id
                        })}
                        id='card-holder'
                    />
                </div>
            </div>
        </div>
    );
};

const translate = translation('payment');

export default translate(PaypalPPCPControls);
